import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import useGoogleMapsLoader from "../../../../Hooks/useGoogleMapsLoader";

const DEFAULT_MAP_CENTER = { lat: 28.613939, lng: 77.209021 };

const mapContainerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: false,
};

const pickerWrapperStyle = (height) => ({
  height,
  width: "100%",
  borderRadius: 8,
  overflow: "hidden",
  border: "1px solid #E0E0E0",
  background: "#F8FAFC",
});

const parseCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isNaN(number) ? null : number;
};

async function reverseGeocode(lat, lng) {
  const googleMaps = window.google?.maps;
  if (googleMaps?.Geocoder) {
    const geocoder = new googleMaps.Geocoder();
    const result = await new Promise((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]?.formatted_address ?? "");
        } else {
          resolve("");
        }
      });
    });
    if (result) return result;
  }
  return "";
}

const ComplaintMapView = ({
  latitude,
  longitude,
  location = "",
  height = "320px",
  showDefaultMap = true,
  defaultCenter = DEFAULT_MAP_CENTER,
  locationLoading = false,
  onLocationSelect,
}) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const position = useMemo(() => {
    const lat = parseCoordinate(latitude);
    const lng = parseCoordinate(longitude);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [latitude, longitude]);

  const mapCenter = useMemo(() => {
    if (position) return position;
    if (!showDefaultMap || !defaultCenter) return null;

    const lat = parseCoordinate(defaultCenter.lat);
    const lng = parseCoordinate(defaultCenter.lng);
    if (lat === null || lng === null) return null;
    return { lat, lng };
  }, [position, showDefaultMap, defaultCenter]);

  const markerPosition = position || mapCenter;

  useEffect(() => {
    if (!mapRef.current || !mapCenter) return;
    mapRef.current.panTo(mapCenter);
  }, [mapCenter?.lat, mapCenter?.lng]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !containerRef.current) return;

    const triggerResize = () => {
      if (!mapRef.current || !window.google?.maps?.event) return;
      window.google.maps.event.trigger(mapRef.current, "resize");
      if (mapCenter) {
        mapRef.current.panTo(mapCenter);
      }
    };

    triggerResize();
    const timer = setTimeout(triggerResize, 300);

    const observer = new ResizeObserver(triggerResize);
    observer.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isLoaded, mapCenter?.lat, mapCenter?.lng]);

  const handleMapClick = async (event) => {
    if (!onLocationSelect) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const address = await reverseGeocode(lat, lng);
    onLocationSelect(lat.toFixed(6), lng.toFixed(6), address);
  };

  if (loadError) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-danger"
        style={pickerWrapperStyle(height)}
      >
        Unable to load Google Maps. Check GOOGLE_MAPS_API_KEY.
      </div>
    );
  }

  if (!mapCenter) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-muted"
        style={pickerWrapperStyle(height)}
      >
        {locationLoading
          ? "Detecting current location..."
          : "Enter valid latitude and longitude to preview the map."}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={pickerWrapperStyle(height)}>
      {!isLoaded ? (
        <div
          className="d-flex align-items-center justify-content-center text-muted"
          style={{ height: "100%", width: "100%", fontSize: 14 }}
        >
          Loading map...
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={15}
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={(map) => {
            mapRef.current = map;
            map.panTo(mapCenter);
          }}
        >
          {markerPosition ? <Marker position={markerPosition} /> : null}
        </GoogleMap>
      )}
    </div>
  );
};

export default ComplaintMapView;
